import {
  addDays,
  addMonths,
  compareLocalDate,
  daysBetween,
  type LocalDate,
} from '../local-date.js';
import {
  DEFAULT_DRILL_RULES,
  DRILL_RULE_CODES,
  type DrillRule,
  type DrillRuleBasis,
} from './rules.js';

/**
 * Tatbikat kural motoru (ADR 0128 §4, Faz 2 Ek): saf fonksiyonlar, saat parametreyle gelir. Gemi
 * sunucusu ve bulut aynı fonksiyonla hesaplar (plan sayfası, pano, zil).
 *
 * - **Next Due** = onaylanmış son uygulama + aralık (takvim ayı; ay sonu kırpılır: 31.01 + 1 ay =
 *   28/29.02). "Ayda bir" takvim ayı içinde değil, son uygulamadan bir ay sonra okunur: kayıt
 *   kaybettirmeyen ve daha sıkı yorum (CLAUDE.md 5).
 * - Yürürlükteki erteleme dönemsel vadeyi taşır; orijinal vade korunur [NR496 4.5.3d].
 * - Mürettebat değişimi tetiği (%25'ten fazla; SOLAS III/19.3.2, ISPS B/13.6) ayrı vade ekler,
 *   dönemsel vadeyi ötelemez; Next Due ikisinden erken olandır.
 * - Kural parametreleri varsayılandır (`DRILL-RULES-1.0`); şirket kural seti (gemi tipi, bayrak ve
 *   SMS koşullu satırlar) üzerine yazar. Kesin doğrulama yetkili mevzuat sorumlusundadır (belge §4).
 */

/** Vadesine bu kadar gün (bugün dahil) kalan tatbikat "Due" görünür. */
export const DRILL_DUE_WINDOW_DAYS = 7;
/** Pano "Upcoming": önümüzdeki 30 gün içinde vadesi gelen (gecikmiş olmayan) tatbikatlar. */
export const DRILL_UPCOMING_DAYS = 30;
/** SOLAS III/19.3.2 ve ISPS B/13.6: mürettebatın %25'inden fazlası (eşit değil). */
export const CREW_CHANGE_THRESHOLD_PERCENT = 25;

export interface DrillVesselProfile {
  /** Geminin tipi (serbest metin, `vessels.vessel_type`). */
  vesselType: string | null;
  /** Bayrak (serbest metin, `vessels.flag`). */
  flag: string | null;
}

const norm = (value: string | null | undefined) =>
  (value ?? '').normalize('NFKC').trim().toLowerCase().replace(/\s+/g, ' ');

/**
 * Yolcu gemisi mi (ADR 0128 §4: yolcu kuralları yalnız yolcu gemisine; kural seti gemi tipine göre
 * süzülür). Gemi tipi serbest metindir: "passenger", "ro-pax", "cruise", "ferry" geçerse yolcu.
 */
export function isPassengerVesselType(type: string | null): boolean {
  return /passenger|ro-?pax|cruise|ferry|yolcu/.test(norm(type));
}

/** Şirketin kural satırı (`drill_rules`): bir varsayılan kuralın gemi tipi / bayrak koşullu değeri. */
export interface CompanyDrillRule {
  id: string;
  /** Varsayılan kuralın kodu (`DRILL_RULE_CODES`). */
  code: string;
  /** Boş: bütün gemi tipleri. Gemi tipinin içinde geçmesi yeter (büyük/küçük harf duyarsız). */
  vesselTypes: readonly string[];
  /** Boş: bütün bayraklar. Tam eşleşme (büyük/küçük harf duyarsız). */
  flags: readonly string[];
  /** Dönemsel kuralda aralık (ay); tetikli kuralda `null`. */
  intervalMonths: number | null;
  /** Tetikli kuralda en geç (saat); dönemsel kuralda `null`. */
  triggerHours: number | null;
  /** `false`: bu koşullarda kural uygulanmaz (yetkili kişinin kararı; gerekçe zorunlu). */
  applicable: boolean;
  reason: string;
  /** Pasif satır hesaba katılmaz (silme yok). */
  active: boolean;
}

export interface EffectiveDrillRule {
  code: string;
  title: string;
  reference: string;
  basis: DrillRuleBasis;
  intervalMonths: number | null;
  triggerHours: number | null;
  applicable: boolean;
  /** `default`: ürünün varsayılanı; `company`: şirketin satırı. */
  source: 'default' | 'company';
  companyRuleId: string | null;
  /** Şirket satırının gerekçesi; varsayılanda kuralın notu. */
  reason: string | null;
}

function matches(rule: CompanyDrillRule, vessel: DrillVesselProfile): boolean {
  const type = norm(vessel.vesselType);
  const flag = norm(vessel.flag);
  const typeOk =
    rule.vesselTypes.length === 0 ||
    rule.vesselTypes.some((t) => norm(t) !== '' && type.includes(norm(t)));
  const flagOk = rule.flags.length === 0 || rule.flags.some((f) => norm(f) === flag && flag !== '');
  return typeOk && flagOk;
}

const specificity = (rule: CompanyDrillRule) =>
  (rule.vesselTypes.length > 0 ? 1 : 0) + (rule.flags.length > 0 ? 1 : 0);

/** Sıkılık: uygulanan kural uygulanmayandan, kısa aralık / kısa tetik uzundan sıkıdır. */
function stricter(a: CompanyDrillRule, b: CompanyDrillRule): number {
  if (a.applicable !== b.applicable) return a.applicable ? -1 : 1;
  const value = (r: CompanyDrillRule) => r.intervalMonths ?? r.triggerHours ?? Infinity;
  return value(a) - value(b) || a.id.localeCompare(b.id);
}

function fromDefault(rule: DrillRule, vessel: DrillVesselProfile): EffectiveDrillRule {
  return {
    code: rule.code,
    title: rule.title,
    reference: rule.reference,
    basis: rule.basis,
    intervalMonths: rule.intervalMonths,
    triggerHours: rule.triggerHours,
    applicable: rule.scope === 'all' || isPassengerVesselType(vessel.vesselType),
    source: 'default',
    companyRuleId: null,
    reason: null,
  };
}

/**
 * Geminin bir kuraldaki etkin değeri: geminin tipine ve bayrağına uyan şirket satırlarından en
 * özgüsü (tip ve bayrak > biri > koşulsuz), eşitlikte en sıkısı; uyan satır yoksa varsayılan (yolcu
 * kuralı yalnız yolcu gemisine). Bilinmeyen kod `null`.
 */
export function effectiveDrillRule(
  code: string | null,
  vessel: DrillVesselProfile,
  companyRules: readonly CompanyDrillRule[],
): EffectiveDrillRule | null {
  const base = DEFAULT_DRILL_RULES.find((r) => r.code === code);
  if (!base) return null;
  const candidates = companyRules
    .filter((r) => r.active && r.code === code && matches(r, vessel))
    .sort((a, b) => specificity(b) - specificity(a) || stricter(a, b));
  const chosen = candidates[0];
  if (!chosen) return fromDefault(base, vessel);
  return {
    ...fromDefault(base, vessel),
    intervalMonths: base.intervalMonths === null ? null : chosen.intervalMonths,
    triggerHours: base.triggerHours === null ? null : chosen.triggerHours,
    applicable: chosen.applicable,
    source: 'company',
    companyRuleId: chosen.id,
    reason: chosen.reason,
  };
}

/** Geminin bütün kuralları (varsayılan sırasıyla). */
export function effectiveDrillRules(
  vessel: DrillVesselProfile,
  companyRules: readonly CompanyDrillRule[],
): EffectiveDrillRule[] {
  return DRILL_RULE_CODES.map((code) => effectiveDrillRule(code, vessel, companyRules)).filter(
    (rule): rule is EffectiveDrillRule => rule !== null,
  );
}

/**
 * Dönemsel kuralın tetik kuralı: yangın ve terk tatbikatı SOLAS'ın mürettebat değişimi kuralına,
 * güvenlik tatbikatı ISPS'inkine bağlıdır (ADR 0128 §4).
 */
export function triggerRuleFor(code: string | null): string | null {
  if (code === 'SOLAS-FIRE' || code === 'SOLAS-ABANDON') return 'SOLAS-CREW-CHANGE';
  if (code === 'ISPS-DRILL') return 'ISPS-CREW-CHANGE';
  return null;
}

/* ---------- Mürettebat değişimi tetiği ---------- */

/** Gemideki görev aralığı (`user_vessel_assignments`: sign-on `from`, sign-off `to`). */
export interface CrewStint {
  userId: string;
  from: LocalDate;
  /** Boş: hâlâ gemide. */
  to: LocalDate | null;
}

export interface CrewChangeTrigger {
  /** Eşiğin aşıldığı gemi günü. */
  triggeredOn: LocalDate;
  /** Tatbikatın en geç günü: tetik + ⌈saat / 24⌉ gün. */
  dueOn: LocalDate;
  newcomers: number;
  onBoard: number;
}

/** Kişinin kesintisiz görevleri birleşir (rol değişimi yeni katılım değildir). */
function mergedStints(assignments: readonly CrewStint[]): CrewStint[] {
  const byUser = new Map<string, CrewStint[]>();
  for (const s of assignments) byUser.set(s.userId, [...(byUser.get(s.userId) ?? []), s]);
  const out: CrewStint[] = [];
  for (const [userId, list] of byUser) {
    const sorted = [...list].sort((a, b) => compareLocalDate(a.from, b.from));
    let current: CrewStint | null = null;
    for (const s of sorted) {
      if (
        current &&
        (current.to === null || compareLocalDate(s.from, addDays(current.to, 1)) <= 0)
      ) {
        current = {
          userId,
          from: current.from,
          to:
            current.to === null || s.to === null
              ? null
              : compareLocalDate(s.to, current.to) > 0
                ? s.to
                : current.to,
        };
      } else {
        if (current) out.push(current);
        current = { userId, from: s.from, to: s.to };
      }
    }
    if (current) out.push(current);
  }
  return out;
}

const onDay = (s: CrewStint, day: LocalDate) =>
  compareLocalDate(s.from, day) <= 0 && (s.to === null || compareLocalDate(s.to, day) >= 0);

/**
 * Mürettebat değişimi tetiği (SOLAS III/19.3.2; ISPS B/13.6): son onaylı uygulamadan (`since`)
 * sonra gemiye katılmış ve gemide olan kişiler, o gün gemide olanların %25'inden fazlaysa tetik o
 * gündür. Katılış ve ayrılış günlerinde (since < gün ≤ bugün) bakılır; ilk aşan gün döner. Tatbikat
 * günü katılan kişi tatbikata katılmış sayılır (aynı gün tatbikatla tetik kapanabilsin).
 * "Limandan kalkıştan sonra 24 saat" sefer verisine bağlanmadı: katılış günü kalkıştan önce ya da
 * aynı gündür, vade daha erken düşer (sıkı okuma; ADR 0128 Faz 2 Ek). Son uygulama yoksa tetik
 * hesaplanmaz (plan zaten başlangıç vadesindedir).
 */
export function crewChangeTrigger(input: {
  assignments: readonly CrewStint[];
  since: LocalDate | null;
  today: LocalDate;
  triggerHours: number;
}): CrewChangeTrigger | null {
  const { since, today } = input;
  if (since === null) return null;
  const stints = mergedStints(input.assignments);
  const days = new Set<LocalDate>();
  for (const s of stints) {
    days.add(s.from);
    if (s.to !== null) days.add(addDays(s.to, 1));
  }
  const ordered = [...days]
    .filter((d) => compareLocalDate(d, since) > 0 && compareLocalDate(d, today) <= 0)
    .sort(compareLocalDate);
  for (const day of ordered) {
    const present = stints.filter((s) => onDay(s, day));
    const newcomers = present.filter((s) => compareLocalDate(s.from, since) > 0).length;
    if (present.length > 0 && newcomers * 100 > present.length * CREW_CHANGE_THRESHOLD_PERCENT) {
      return {
        triggeredOn: day,
        dueOn: addDays(day, Math.max(1, Math.ceil(input.triggerHours / 24))),
        newcomers,
        onBoard: present.length,
      };
    }
  }
  return null;
}

/* ---------- Vade ---------- */

export interface DrillDueInput {
  /** Hiç onaylı uygulama yokken ilk vade (planın başlangıcı). */
  startOn: LocalDate;
  /** Planın aralığı (ay). */
  intervalMonths: number;
  /** Onaylanmış son uygulama. */
  lastConductedOn: LocalDate | null;
  /** Bu döngüde yürürlükteki (onaylı, sona ermemiş) ertelemenin yeni vadesi. */
  postponedTo: LocalDate | null;
  trigger: CrewChangeTrigger | null;
}

export type DrillDueBasis = 'start' | 'interval' | 'postponed' | 'crew_change';

export interface DrillDue {
  /** Ertelenmemiş vade (son uygulama + aralık ya da başlangıç) [NR496 4.5.3d]. */
  originalDueOn: LocalDate;
  /** Ertelemeyle taşınmış dönemsel vade. */
  periodicDueOn: LocalDate;
  triggerDueOn: LocalDate | null;
  /** Kural motorunun vadesi: dönemsel ve tetik vadesinden erken olan. */
  nextDueOn: LocalDate;
  basis: DrillDueBasis;
}

export function drillDue(input: DrillDueInput): DrillDue {
  const originalDueOn =
    input.lastConductedOn === null
      ? input.startOn
      : addMonths(input.lastConductedOn, input.intervalMonths);
  const periodicDueOn = input.postponedTo ?? originalDueOn;
  const triggerDueOn = input.trigger?.dueOn ?? null;
  const triggerFirst = triggerDueOn !== null && compareLocalDate(triggerDueOn, periodicDueOn) < 0;
  return {
    originalDueOn,
    periodicDueOn,
    triggerDueOn,
    nextDueOn: triggerFirst ? triggerDueOn : periodicDueOn,
    basis: triggerFirst
      ? 'crew_change'
      : input.postponedTo !== null
        ? 'postponed'
        : input.lastConductedOn === null
          ? 'start'
          : 'interval',
  };
}

/* ---------- Durum ---------- */

/**
 * Belge §2'nin durumları. Faz 2'de kullanılanlar: Draft, Planned, Due, Pending Review (onay
 * bekleyen kayıtlı uygulama), Completed (dönem görünümünde yapılmış uygulama), Overdue, Postponed,
 * Cancelled. In Progress Faz 3'ün icra kaydıyla (süren kayıt) gelir.
 */
export const DRILL_STATUSES = [
  'draft',
  'planned',
  'due',
  'in_progress',
  'pending_review',
  'completed',
  'overdue',
  'postponed',
  'cancelled',
] as const;
export type DrillStatus = (typeof DRILL_STATUSES)[number];

/** Planın kalıcı hâli: taslak, yürürlükte, iptal. */
export const DRILL_PLAN_STATES = ['draft', 'active', 'cancelled'] as const;
export type DrillPlanState = (typeof DRILL_PLAN_STATES)[number];

/**
 * Planın bugünkü durumu. Öncelik: iptal > taslak > inceleme bekleyen uygulama > gecikmiş > süren
 * icra kaydı (Faz 3) > ertelenmiş > vadesi yakın (7 gün) > planlı. Ertelenmiş ama yeni vadesi de
 * geçmiş plan gecikmiştir; süren kayıt gecikmeyi örtmez (sıkı seçenek, ADR 0128 Faz 3 Ek).
 */
export function drillPlanStatus(input: {
  state: DrillPlanState;
  nextDueOn: LocalDate;
  today: LocalDate;
  pendingReview: boolean;
  postponed: boolean;
  /** Planın süren (In Progress) icra kaydı var (Faz 3). */
  inProgress?: boolean;
}): DrillStatus {
  if (input.state === 'cancelled') return 'cancelled';
  if (input.state === 'draft') return 'draft';
  if (input.pendingReview) return 'pending_review';
  const left = daysBetween(input.today, input.nextDueOn);
  if (left < 0) return 'overdue';
  if (input.inProgress) return 'in_progress';
  if (input.postponed) return 'postponed';
  return left <= DRILL_DUE_WINDOW_DAYS ? 'due' : 'planned';
}

/* ---------- Döngüler ve uyum oranı ---------- */

export type DrillCycleResult = 'on_time' | 'late' | 'missed' | 'open';

export interface DrillCycle {
  /** Döngüyü başlatan onaylı uygulama; ilk döngüde `null` (planın başlangıcı). */
  anchorOn: LocalDate | null;
  dueOn: LocalDate;
  /** Döngüyü kapatan sonraki onaylı uygulama. */
  closedOn: LocalDate | null;
  result: DrillCycleResult;
}

/**
 * Planın döngüleri (dönemsel completion rate): her onaylı uygulama yeni döngü başlatır, vadesi
 * uygulama + aralık ya da o döngünün onaylı ertelemesi (`postponedDue`, anahtar döngünün başladığı
 * uygulamanın günü). Vadesinde ya da önce kapanan `on_time`, sonra kapanan `late`, vadesi geçip
 * kapanmayan `missed`, vadesi gelmemiş `open`. İlk uygulama planın başlangıcından önceyse (başlangıç
 * kaydı) başlangıç döngüsü yoktur.
 */
export function drillCycles(input: {
  startOn: LocalDate;
  intervalMonths: number;
  /** Onaylı uygulamaların günleri. */
  conducts: readonly LocalDate[];
  postponedDue: ReadonlyMap<string, LocalDate>;
  today: LocalDate;
}): DrillCycle[] {
  const conducts = [...new Set(input.conducts)].sort(compareLocalDate);
  const anchors: (LocalDate | null)[] = [];
  const first = conducts[0];
  if (first === undefined || compareLocalDate(first, input.startOn) > 0) anchors.push(null);
  anchors.push(...conducts);
  return anchors.map((anchorOn) => {
    const dueOn =
      (anchorOn !== null ? input.postponedDue.get(anchorOn) : input.postponedDue.get('')) ??
      (anchorOn === null ? input.startOn : addMonths(anchorOn, input.intervalMonths));
    const closedOn =
      conducts.find((c) => anchorOn === null || compareLocalDate(c, anchorOn) > 0) ?? null;
    const result: DrillCycleResult =
      closedOn !== null
        ? compareLocalDate(closedOn, dueOn) <= 0
          ? 'on_time'
          : 'late'
        : compareLocalDate(dueOn, input.today) < 0
          ? 'missed'
          : 'open';
    return { anchorOn, dueOn, closedOn, result };
  });
}

/**
 * Dönemin uyum oranı (yüzde, tam sayı): vadesi dönemde olan ve sonucu belli döngülerden vadesinde
 * yapılanların payı. Hesaba giren döngü yoksa `null`.
 */
export function completionRate(
  cycles: readonly DrillCycle[],
  range: { from: LocalDate; to: LocalDate },
): { onTime: number; counted: number; percent: number | null } {
  const inRange = cycles.filter(
    (c) =>
      c.result !== 'open' &&
      compareLocalDate(c.dueOn, range.from) >= 0 &&
      compareLocalDate(c.dueOn, range.to) <= 0,
  );
  const onTime = inRange.filter((c) => c.result === 'on_time').length;
  return {
    onTime,
    counted: inRange.length,
    percent: inRange.length === 0 ? null : Math.round((onTime * 100) / inRange.length),
  };
}
